/**
 * PlayButton Component
 *
 * Button that allows players to mark themselves as ready to start the game.
 *
 * Features:
 * - Enabled only when all 5 pieces are placed
 * - Sends SET_READY message to PartyKit server
 * - Shows different states: disabled, ready to play, waiting
 * - Integrates with game store for state management
 *
 * States:
 * - Disabled: Not all pieces placed yet
 * - Enabled: All pieces placed, can click to be ready
 * - Ready/Waiting: Player clicked ready, waiting for opponent
 * - Hidden: Game has started (playing or ended phase)
 *
 * Phase 6.2 Implementation
 */

import { useGameStore } from '../stores/gameStore';
import { COPY } from '../copy';

export default function PlayButton({ onReady }) {
  const gamePhase = useGameStore((state) => state.gamePhase);
  const myPlacedPieces = useGameStore((state) => state.myPlacedPieces);
  const playerId = useGameStore((state) => state.playerId);
  const player1 = useGameStore((state) => state.player1);
  const player2 = useGameStore((state) => state.player2);
  const canSetReady = useGameStore((state) => state.canSetReady);

  // Determine if current player is ready
  const isPlayerReady = () => {
    if (player1?.id === playerId) {
      return player1?.ready === true;
    }
    if (player2?.id === playerId) {
      return player2?.ready === true;
    }
    return false;
  };

  const playerReady = isPlayerReady();

  // Don't show button if game has already started or ended
  if (gamePhase === 'playing' || gamePhase === 'ended') {
    return null;
  }

  // Check if all 5 pieces are placed
  const allPiecesPlaced = myPlacedPieces.length === 5;

  // Button should be enabled if:
  // - All pieces are placed
  // - Player is not already ready
  // - Game is in setup or waiting phase
  const isEnabled = allPiecesPlaced && !playerReady && canSetReady();

  const handleClick = () => {
    if (isEnabled && onReady) {
      onReady();
    }
  };

  // Button text based on state
  let buttonText = COPY.placement.buttons.play;
  if (!allPiecesPlaced) {
    buttonText = COPY.placement.buttons.placeAll(myPlacedPieces.length, 5);
  } else if (playerReady) {
    buttonText = COPY.placement.buttons.ready;
  }

  // Button styling based on state
  const baseClasses =
    'font-bold py-3 px-8 rounded-lg transition-all duration-300 text-lg border-2';
  const enabledClasses =
    'bg-neon-cyan text-dark-navy border-white/70 hover:bg-neon-pink hover:border-white/70 hover:text-white shadow-glow-cyan';
  const disabledClasses =
    'bg-dark-purple/50 text-gray-500 border-gray-600 cursor-not-allowed';
  const readyClasses =
    'bg-neon-green text-dark-navy border-white/70 cursor-default shadow-glow-green';

  let buttonClasses = baseClasses;
  if (playerReady) {
    buttonClasses += ` ${readyClasses}`;
  } else if (isEnabled) {
    buttonClasses += ` ${enabledClasses}`;
  } else {
    buttonClasses += ` ${disabledClasses}`;
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <button
        type="button"
        onClick={handleClick}
        disabled={!isEnabled}
        className={buttonClasses}
        aria-label={buttonText}
        data-testid="play-button"
      >
        {buttonText}
      </button>

      {!allPiecesPlaced && (
        <p className="text-sm text-gray-400" data-testid="placement-hint">
          {COPY.placement.hints.placeAll}
        </p>
      )}

      {playerReady && (
        <p
          className="text-sm text-neon-green font-semibold animate-pulse"
          data-testid="ready-status"
        >
          {COPY.placement.hints.waitingForOpponent}
        </p>
      )}
    </div>
  );
}
